/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-06-13 15:22:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Card, Tooltip, TextStyle, TextField, Button, Icon, InlineError } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { MobileCancelMajor } from '@shopify/polaris-icons';
import { URL } from 'src/constants/links';
import MainMenu from 'src/components/containers/main-menu';
import TitleHelp from 'src/components/views/common/title-help';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import RemoveIpIgnoreConfirmModal from './remove-ip-ignore-confirm-modal';

type Props = WithI18nProps;
type State = {
  actionSurvey: boolean,
  youtubeModal: boolean
};

class AnalyticSettings extends Component<Props, State> {
  state = {
    ipTextAdding: "",
    ipTextRemoving: "",
    isAdding: false,
    isRemoving: false,
    activeModalRemoveIpIgnoreConfirm: false,
  };

  componentDidMount() {
    const { getAnalyticsSettings } = this.props;
    getAnalyticsSettings();
  }

  onAddIpIgnore = () => {
    const { updateAnalyticsSettings, ignoreIPs } = this.props;
    const { ipTextAdding } = this.state;
    this.setState({
      isAdding: true
    });
    updateAnalyticsSettings({
      ignoreIPs: [ipTextAdding, ...ignoreIPs]
    });
  }

  handleRemoveIp = () => {
    const { ipTextRemoving } = this.state;
    const { updateAnalyticsSettings, ignoreIPs } = this.props;
    this.setState({
      isRemoving: true
    })
    updateAnalyticsSettings({
      ignoreIPs: ignoreIPs.filter(ip => ip !== ipTextRemoving)
    });
  }

  onRemoveIp = (ipRemove) => {
    this.toggleChangeState('activeModalRemoveIpIgnoreConfirm');
    this.setState({
      ipTextRemoving: ipRemove,
    });
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.loading && nextProps.loading !== this.props.loading) {
      this.setState({
        ipTextAdding: '',
        ipTextRemoving: '',
        isAdding: false,
        isRemoving: false,
        activeModalRemoveIpIgnoreConfirm: false
      })
    }
  }

  isValidIp = (ip) => {
    if (!ip) {
      return true;
    }
    if (/^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/.test(ip)) {
      return true
    }
    return false;
  }

  isExisted = (ipCheck) => {
    const { ignoreIPs } = this.props;
    return ignoreIPs.find(ip => ip === ipCheck);
  }

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  render() {
    const {
      i18n,
      ignoreIPs,
      loading
    } = this.props;

    const {
      ipTextAdding,
      ipTextRemoving,
      isAdding,
      isRemoving,
      activeModalRemoveIpIgnoreConfirm
    } = this.state;

    const isInvalid = !this.isValidIp(ipTextAdding);
    const isExisted = this.isExisted(ipTextAdding);
    const errorMessage = isInvalid
      ? i18n.translate('AdminHTML.Analytics.AnalyticsV2.analyticSettings_Err_invalid_ip')
      : (
        isExisted ? i18n.translate('AdminHTML.Analytics.AnalyticsV2.analyticSettings_Err_duplicate_ip') : ''
      );  

      return (
      <div className="BC-Page BC-Page__AnalyticSettings">
        <MainMenu menuId="menu-analytics-settings" />
        <Page
          title={i18n.translate('AdminHTML.Analytics.AnalyticsV2.analyticSettings_PageTitle')}>
          <TitleHelp i18n={i18n} hrefUserManual={URL['Link_V2_Analytics_Settings_ManualGuide']} />
          <Layout>
            <Layout.Section>
            </Layout.Section>
            <Layout.Section>
              <Card>
                <Card.Section>
                  <div>
                    <Tooltip
                      preferredPosition="above"
                      content={i18n.translate(
                        'AdminHTML.Analytics.AnalyticsV2.analyticSettings_Tooltip'
                      )}>
                      <TextStyle>
                        <h2 className="Polaris-Heading">
                          {i18n.translate(
                            'AdminHTML.Analytics.AnalyticsV2.analyticSettings_Title'
                          )}
                        </h2>
                      </TextStyle>
                    </Tooltip>
                  </div>
                </Card.Section>
                <Card.Section>
                  <div className='BC-Page__AnalyticSettings__IPs__Action'>
                    <TextField
                      id='ip-text'
                      error={!(!isInvalid && !isExisted)}
                      onChange={value => this.setState({ ipTextAdding: value })}
                      value={ipTextAdding}
                      placeholder={i18n.translate('AdminHTML.Analytics.AnalyticsV2.analyticSettings_Placehoder')}
                    />
                    <Button
                      loading={loading && isAdding} 
                      primary
                      onClick={this.onAddIpIgnore}
                      disabled={!(ipTextAdding.length && this.isValidIp(ipTextAdding) && !this.isExisted(ipTextAdding))}
                    >{i18n.translate('AdminHTML.Common.Action.add')}</Button>
                  </div>
                  <div>
                  <InlineError message={errorMessage} fieldID='ip-text' />
                  </div>
                  <div className='BC-Page__AnalyticSettings__IPs__List'>
                    {
                      ignoreIPs.map(ip => {
                        return (
                          <div key={ip} className='BC-Page__AnalyticSettings__IPs__Item'>
                            <div className="ip-text">{ip}</div>
                            <div className="remove" onClick={() => this.onRemoveIp(ip)}>
                              {
                                loading && isRemoving && (ip === ipTextRemoving) ?
                                <div className="loading" /> :
                                <Icon
                                  source={MobileCancelMajor}
                                  color="base"
                                />
                              }
                            </div>
                          </div>
                        )
                      })
                    }
                  </div>
                </Card.Section>
              </Card>
            </Layout.Section>
            <RemoveIpIgnoreConfirmModal
              active={activeModalRemoveIpIgnoreConfirm}
              handleCancel={() => this.toggleChangeState('activeModalRemoveIpIgnoreConfirm')}
              handleRemoveIp={this.handleRemoveIp}
            />
          </Layout>
        </Page>
      </div>
    );
  }

}

const mapStateToProps = (state, props) => {
  return {
    ignoreIPs: _.get(state.analyticsV2, 'analyticsSettings.ignoreIPs', []),
    loading: _.get(state.analyticsV2, 'savingAnalyticsSettings', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getAnalyticsSettings: () => dispatch(AnalyticsActions.getAnalyticsSettings(ownProps.i18n)),
  updateAnalyticsSettings: (analyticsSettings) =>
    dispatch(AnalyticsActions.updateAnalyticsSettings(analyticsSettings, ownProps.i18n))
});


export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(AnalyticSettings));
